/* eslint-disable @typescript-eslint/naming-convention */

import { useCallback, useContext, useEffect, useMemo } from 'react';

import { BulkUserCreationData } from '../BulkUserCreation';
import { BULK_USER_TRACK_EVENTS } from '../BulkUserCreation.decl';
import { BulkUserCreationContext } from '../BulkUserCreationProvider/BulkUserCreationProvider';

import { ReactComponent as ErrorSVG } from './assets/error.svg';
import { ReactComponent as FailureSVG } from './assets/failure.svg';
import { ReactComponent as SuccessSVG } from './assets/success.svg';
import {
  BulkUserCreationRecapPartialProps,
  BulkUserCreationRecapSuccessProps,
  BulkUserCreationRecapFailureProps,
  UsersCount,
} from './BulkUserCreationRecap.decl';
import { BulkUserCreationRecapContent } from './BulkUserCreationRecapContent';

import { ExternalLink } from '@components/Link';
import { capitalizeFirstLetter, FormWizard, useFormWizard } from '@dashboard/library';
import { BulkUserError } from '@generated/globalTypes';
import { useTracker } from '@hooks/useTracker';
import { Trans, useTranslation } from 'react-i18next';

export function BulkUserCreationRecapSuccess({
  response,
  usersCount,
}: BulkUserCreationRecapSuccessProps) {
  const { t } = useTranslation();
  const { track } = useTracker();
  const context = useContext(BulkUserCreationContext);

  useEffect(() => {
    track({
      event: BULK_USER_TRACK_EVENTS.SUCCEEDED,
      payload: { ...context.state, n_users_imported: usersCount ? usersCount.created : null },
    });
  }, [context.state, track, usersCount]);

  return (
    <BulkUserCreationRecapContent
      image={<SuccessSVG />}
      title={t('bulk_user_creation.recap.success.text')}
    >
      {t('bulk_user_creation.recap.success.subtext', { createdCount: response.createdCount })}
    </BulkUserCreationRecapContent>
  );
}

export function BulkUserCreationRecapPartial({
  response,
  usersCount,
}: BulkUserCreationRecapPartialProps) {
  const { t } = useTranslation();
  const { track } = useTracker();
  const context = useContext(BulkUserCreationContext);

  useEffect(() => {
    track({
      event: BULK_USER_TRACK_EVENTS.PARTIALLY_SUCCEEDED,
      payload: {
        ...context.state,
        n_users_failed: usersCount ? usersCount.failed : null,
        n_users_imported: usersCount ? usersCount.created : null,
      },
    });
  }, [context.state, track, usersCount]);

  const totalCount = response?.totalCount || 0;
  const createdCount = response?.createdCount || 0;
  const missingCount = totalCount - createdCount;

  return (
    <BulkUserCreationRecapContent
      image={<ErrorSVG />}
      title={t('bulk_user_creation.recap.partial.text', {
        missingCount,
        totalCount,
      })}
    >
      <Trans
        i18nKey='bulk_user_creation.recap.partial.subtext'
        values={{ createdCount, missingCount }}
      >
        <br />
      </Trans>
    </BulkUserCreationRecapContent>
  );
}

export function BulkUserCreationRecapFailure({
  response,
  usersCount,
}: BulkUserCreationRecapFailureProps) {
  const { t } = useTranslation();
  const { track } = useTracker();
  const context = useContext(BulkUserCreationContext);

  useEffect(() => {
    track({
      event: BULK_USER_TRACK_EVENTS.FAILED,
      payload: { ...context.state, n_users_failed: usersCount ? usersCount.failed : null },
    });
  }, [context.state, track, usersCount]);

  function getErrorText() {
    if (response?.error === BulkUserError.CSV_FORMAT_VALIDATION_ERROR) {
      return t('bulk_user_creation.recap.failure.csv_format_validation_error');
    }
    if (response?.error === BulkUserError.CSV_VALIDATION_ERROR) {
      return t('bulk_user_creation.recap.failure.csv_validation_error');
    }
    return t('bulk_user_creation.recap.failure.unknown_error');
  }

  return (
    <BulkUserCreationRecapContent
      image={<FailureSVG />}
      title={t('bulk_user_creation.recap.failure.text')}
    >
      {getErrorText()}
    </BulkUserCreationRecapContent>
  );
}

export function BulkUserCreationRecap() {
  const { t } = useTranslation();
  const { onClose, data, editData, setPageIndex } = useFormWizard<BulkUserCreationData>();
  const { response } = data;
  const { track } = useTracker();
  const context = useContext(BulkUserCreationContext);

  const usersCount: UsersCount = useMemo(() => {
    const usersTotalCount = response?.totalCount ?? null;
    const usersCreatedCount = response?.createdCount ?? null;

    if (usersTotalCount === null || usersCreatedCount === null) {
      return null;
    }

    const usersFailedCount = usersTotalCount - usersCreatedCount;

    return {
      total: usersTotalCount,
      created: usersCreatedCount,
      failed: usersFailedCount,
    };
  }, [response?.createdCount, response?.totalCount]);

  const isSuccess = response?.error === null;
  const isFailure =
    !response ||
    response.error === BulkUserError.CSV_VALIDATION_ERROR ||
    response.error === BulkUserError.CSV_FORMAT_VALIDATION_ERROR ||
    usersCount?.created === 0;
  const isPartial = response?.error === BulkUserError.USERS_VALIDATION_ERROR;

  const handleStartAgain = useCallback(() => {
    const previousFunnelId = context.state.funnel_id;
    const newFunnelId = context.actions.initFunnelId();

    track({
      event: BULK_USER_TRACK_EVENTS.STARTED_AGAIN,
      payload: {
        ...context.state,
        funnel_id: newFunnelId,
        previous_funnel_id: previousFunnelId,
        n_users_failed: usersCount ? usersCount.failed : null,
        n_users_imported: usersCount ? usersCount.created : null,
      },
    });
    editData({ file: null, response: null });
    setPageIndex(0);
  }, [context.actions, context.state, editData, setPageIndex, track, usersCount]);

  return (
    <>
      <FormWizard.PageHeader>
        <FormWizard.PageHeaderTitle>
          {t('bulk_user_creation.recap.title')}
        </FormWizard.PageHeaderTitle>
      </FormWizard.PageHeader>

      <FormWizard.PageContent>
        {isSuccess && <BulkUserCreationRecapSuccess response={response} usersCount={usersCount} />}
        {isPartial && <BulkUserCreationRecapPartial response={response} usersCount={usersCount} />}
        {isFailure && <BulkUserCreationRecapFailure response={response} usersCount={usersCount} />}
      </FormWizard.PageContent>

      <FormWizard.PageFooter>
        <FormWizard.PageFooterPrev onClick={onClose}>
          {capitalizeFirstLetter(t('generic.close'))}
        </FormWizard.PageFooterPrev>

        {!isSuccess && (
          <FormWizard.PageFooterNext onClick={handleStartAgain}>
            {capitalizeFirstLetter(t('bulk_user_creation.recap.start_again'))}
          </FormWizard.PageFooterNext>
        )}
      </FormWizard.PageFooter>

      <FormWizard.PageSubText>
        <Trans i18nKey='bulk_user_creation.contact_support'>
          <ExternalLink href={t('bulk_user_creation.contact_support_link')} />
        </Trans>
      </FormWizard.PageSubText>
    </>
  );
}
